'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import styles from './estimation.module.scss';
import {
  Button,
  Container,
  Input,
  Notification,
  Space,
  Textarea,
  Radio,
  Group,
  Accordion,
  Title as MantineTitle,
  Divider,
  Stepper,
  Slider,
  Tooltip,
  Checkbox,
} from '@mantine/core';
import { Dropzone } from '@/src/components/EditorText/Dropzone';
import FullScreenImageHalf from '@/src/components/SliceMachine/FullScreenImageHalf/FullScreenImageHalf';
import { Title } from '@/src/components/Title/Title';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';

export default function Estimation() {
  const dictionary = useDictionary();
  const router = useRouter();

  const [mainImage, setMainImage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [housingType, setHousingType] = useState('');
  const [vehicleSizeIndex, setVehicleSizeIndex] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<null | { message: string; color: 'red' | 'green' }>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    phone: false,
    vehicleType: false,
    vehicleSize: false,
    housingType: false,
    comment: false,
    accepted: false,
  });

  const form = dictionary.pages.estimations.form;
  const steps = form.steps;
  const vehicleMarks = form.slider.marks;
  const vehicleSize = vehicleSizeIndex !== null ? vehicleMarks[vehicleSizeIndex].label : '';

  useEffect(() => {
    if (!name || !email || !phone) setActiveStep(0);
    else if (!vehicleType) setActiveStep(1);
    else if (!vehicleSize) setActiveStep(2);
    else if (!housingType) setActiveStep(3);
    else if (!comment) setActiveStep(4);
    else setActiveStep(4);

    const hasErrors = {
      name: !name,
      email: !email,
      phone: !phone,
      vehicleType: !vehicleType,
      vehicleSize: vehicleSize === '',
      housingType: !housingType,
      comment: !comment,
      accepted: !accepted,
    };

    setErrors(hasErrors);

    setErrors(hasErrors);
  }, [name, email, phone, vehicleType, housingType, vehicleSize, mainImage, comment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const hasErrors = {
      name: !name,
      email: !email,
      phone: !phone,
      vehicleType: !vehicleType,
      vehicleSize: vehicleSize === '',
      housingType: !housingType,
      comment: !comment,
      accepted: !accepted,
    };

    setErrors(hasErrors);

    const hasAnyError = Object.values(hasErrors).some(Boolean);
    if (hasAnyError) {
      setNotification({ message: dictionary.pages.estimations.messages.required, color: 'red' });
      setLoading(false);
      return;
    }

    const estimationData = {
      name,
      email,
      phone,
      vehicleType,
      housingType,
      vehicleSize,
      mainImage,
      comment,
    };

    try {
      const response = await fetch('/api/estimation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(estimationData),
      });

      setLoading(false);

      if (response.ok) {
        setNotification({ message: dictionary.pages.estimations.messages.success, color: 'green' });
        setTimeout(() => router.push('/articles'), 2000);
      } else {
        setNotification({ message: dictionary.pages.estimations.messages.error, color: 'red' });
      }
    } catch (err) {
      console.error('Estimation error:', err);
      setLoading(false);
      setNotification({ message: dictionary.pages.estimations.messages.server, color: 'red' });
    }
  };

  return (
    <>
      {/* @ts-ignore */}
      <FullScreenImageHalf data={dictionary.pages.estimations.slices[0]} />
      <Container>
        <Container my="xl" py="xl" className={styles.container}>
          <div className={styles.stepper}>
            <Stepper active={activeStep} orientation="vertical">
              {steps.map((label: string, idx: number) => (
                <Stepper.Step key={idx} label={label} />
              ))}
            </Stepper>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <Container style={{ textAlign: 'center' }}>
              <Title title={dictionary.pages.estimations.cta} />
            </Container>

            {notification && (
              <Notification color={notification.color} onClose={() => setNotification(null)}>
                {notification.message}
              </Notification>
            )}

            <Divider my="md" label={steps[0]} labelPosition="center" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={form.fields.name.placeholder} error={errors.name} />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={form.fields.email.placeholder} error={errors.email} type="email" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={form.fields.phone.placeholder} error={errors.phone} type="tel" />

            <Divider my="md" label={steps[1]} labelPosition="center" />
            <Radio.Group value={vehicleType} onChange={setVehicleType}>
              <Group>
                {form.firstRadioChoice.options.map((opt: any) => (
                  <Radio key={opt.value} value={opt.value} label={opt.label} />
                ))}
              </Group>
              {errors.vehicleType && <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.8rem' }}>{form.fields.required}</div>}
            </Radio.Group>

            <Divider my="md" label={steps[2]} labelPosition="center" />
            <Tooltip label={form.slider.tooltip} withArrow>
              <MantineTitle order={5}>{form.slider.title}</MantineTitle>
            </Tooltip>
            <Slider
              value={vehicleSizeIndex ?? 0}
              onChange={(val) => {
                setVehicleSizeIndex(val);
                setErrors((prev) => ({ ...prev, vehicleSize: false }));
              }}
              marks={vehicleMarks}
              min={0}
              max={vehicleMarks.length - 1}
              step={1}
              size="lg"
              label={(val) => vehicleMarks[val].label}
              my="md"
            />
            {errors.vehicleSize && <div style={{ color: 'red', marginTop: '2rem', fontSize: '0.8rem' }}>{form.fields.required}</div>}

            <Divider my="md" label={steps[3]} labelPosition="center" />
            <Radio.Group value={housingType} onChange={setHousingType}>
              <Group>
                {form.secondRadioChoice.options.map((opt: any) => (
                  <Radio key={opt.value} value={opt.value} label={opt.label} />
                ))}
              </Group>
              {errors.housingType && <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.8rem' }}>{form.fields.required}</div>}
            </Radio.Group>

            <Divider my="md" label={steps[4]} labelPosition="center" />
            <Textarea
              placeholder={form.fields.comment.placeholder}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              autosize
              minRows={4}
              error={errors.comment}
            />

            <Divider my="md" label={form.fields.image} labelPosition="center" />
            <Accordion variant="separated">
              <Accordion.Item value="dropzone">
                <Accordion.Control style={{ fontSize: '0.9rem' }}>{form.fields.image}</Accordion.Control>
                <Accordion.Panel>
                  <Dropzone setImageUrl={setMainImage} imageUrl={mainImage} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Checkbox
              mt="lg"
              checked={accepted}
              onChange={(event) => setAccepted(event.currentTarget.checked)}
              label={form.fields.consentLabel}
              error={errors.accepted}
            />

            <Button type="submit" disabled={loading} className={styles.button} fullWidth>
              {loading ? form.loading : form.submit}
            </Button>
          </form>
          <p>{form.fields.legalNotice}</p>
        </Container>
      </Container>
    </>
  );
}
